'use client';

import { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { authClient } from '../utils/auth-client';
import { connectNativePostHog } from '../utils/posthogNativeSession';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = authClient.useSession();
  const initialized = useRef(false);
  const nativeSession = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return;
    return connectNativePostHog(posthog, options => {
      nativeSession.current = !!options.bootstrap;
      if (!initialized.current) {
        posthog.init(key, {
          api_host: '/ingest',
          ui_host: 'https://us.posthog.com',
          defaults: '2025-05-24',
          capture_exceptions: true,
          debug: process.env.NODE_ENV === 'development',
          ...options,
        });
        initialized.current = true;
      } else {
        // React Strict Mode reconnects effects without recreating the SDK.
        posthog.set_config({ before_send: options.before_send });
      }
      setReady(true);
    });
  }, []);

  useEffect(() => {
    if (!ready || isPending || nativeSession.current) return;
    if (session?.user) {
      // Extract email domain for filtering (e.g., "gmail.com")
      const emailDomain = session.user.email
        ? session.user.email.split('@')[1]
        : undefined;

      // Identify the user in PostHog with user ID and email domain
      // This links all events to the same user and allows filtering by domain
      // without storing the full email address
      posthog.identify(session.user.id, {
        email_domain: emailDomain,
      });
    } else if (posthog.get_property('$user_id')) {
      // Only a confirmed sign-out should end a session, not an auth refetch.
      posthog.reset();
    }
  }, [ready, isPending, session?.user?.id, session?.user?.email]);

  return (
    <PHProvider client={posthog}>
      {children}
    </PHProvider>
  );
}

